import React from "react";
import { Card, CardContent, Typography, Button, Box } from "@mui/material";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Link } from "react-router-dom";

export default function StockCard({ stock, isAuthenticated, inPortfolio, onAdd }) {
  const { ticker, financials } = stock.financial_data;
  const prices = stock.historicPrices ?? {};

  const lastFinancialYear = Math.max(...financials.map(f => f.Year));

// ---------- Graph data ----------
  const data = [...financials]
    .sort((a, b) => a.Year - b.Year)
    .map((f) => ({
      year: f.Year,
      netIncome: f.netIncome,
      price: f.Year === lastFinancialYear
        ? Number(stock.lastPrice.toFixed(2))
        : prices[String(f.Year)] ?? null,
  }));

  // ---------- Button style ----------
  const actionButtonStyle = {
    fontSize: "12px",
    padding: "4px 10px",
    borderRadius: "6px",
    minWidth: "auto",
    height: "32px",
    background: "linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)",
    color: "#fff",
    whiteSpace: "nowrap",
    "&:hover": {
      background: "linear-gradient(90deg, #1565c0 0%, #1e88e5 100%)",
    },
    "&.Mui-disabled": {
      background: "#555",
      color: "#ccc",
    },
  };

  return (
    <Card
      sx={{
        backgroundColor: "#1e1e1e",
        color: "#fff",
        width: "360px",
        height: "600px",
        margin: "0 auto",
        borderRadius: "16px",
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        boxSizing: "border-box",
      }}
    >
      <CardContent
        sx={{
          flex: "1 1 auto",
          padding: "12px",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        <Typography
          variant="h6"
          gutterBottom
          sx={{ whiteSpace: "normal", overflowWrap: "anywhere", wordBreak: "break-word" }}
        >
          {stock?.info?.longName?.length > 30
            ? stock.info.longName.substring(0, 25) + "..."
            : stock?.info?.longName}
        </Typography>

        <Typography variant="h6">{ticker}</Typography>
        <Typography variant="body1">Last Price: ${stock.lastPrice.toFixed(2)}</Typography>
        <Typography variant="body1">{stock.info.industryKey}</Typography>
        <Typography variant="body2" sx={{ color: "lightgreen", marginBottom: "10px" }}>
          Net Income vs Price
        </Typography>

        {/* ---------- Graph ---------- */}
        <div style={{ flexGrow: 1, height: "280px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <XAxis dataKey="year" stroke="#fff" />
              <YAxis yAxisId="left" stroke="#fff" tickFormatter={v => `$${(v / 1_000_000).toFixed(0)}M`} />
              <YAxis yAxisId="right" orientation="right" stroke="#1e90ff" tickFormatter={v => `$${v.toFixed(0)}`} />
              <Tooltip contentStyle={{ backgroundColor: "#2c2c2c", color: "#fff", borderRadius: "8px", border: "none" }} />
              <Line yAxisId="left" type="natural" dataKey="netIncome" stroke="lightgreen" strokeWidth={3} dot={{ r: 2 }} />
              <Line yAxisId="right" type="natural" dataKey="price" stroke="#1e90ff" strokeWidth={3} dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* ---------- Action buttons ---------- */}
        <Box sx={{ display: "flex", gap: "8px", marginTop: "6px", flexWrap: "nowrap" }}>
          {isAuthenticated && (
            <Button
              variant="contained"
              onClick={onAdd}
              disabled={inPortfolio}
              sx={actionButtonStyle}
            >
              {inPortfolio ? "In Portfolio" : "Add"}
            </Button>
          )}

          <Button
            variant="contained"
            component={Link}
            to={`/stock/${ticker}`}
            sx={actionButtonStyle}
          >
            Details
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
